import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import teamImageService from '../services/teamImageService';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

/**
 * TeamLogo Component - Displays team logos with fallback handling
 *
 * Handles three types of logos:
 * 1. New format: /images/teams/{id}/logo.jpeg — used directly as <img src>
 * 2. Legacy format: bare filename (e.g. logo_1234.png) — fetched via presigned URL
 * 3. Emoji/icon logos — displayed directly
 * 4. Fallback to first letter of team name
 *
 * @param {Object} props
 * @param {number} props.teamId - Team ID (for legacy logo loading)
 * @param {string} props.teamLogo - Team logo (/images/... path, bare filename, or emoji)
 * @param {string} props.teamName - Team name for fallback display
 * @param {string} props.size - Tailwind size classes (default: 'w-10 h-10')
 * @param {string} props.textSize - Tailwind text size for fallback (default: 'text-sm')
 * @param {string} props.rounded - Tailwind rounded classes (default: 'rounded-lg')
 */
const TeamLogo = ({
  teamId,
  teamLogo,
  teamName,
  size = 'w-10 h-10',
  textSize = 'text-sm',
  rounded = 'rounded-lg'
}) => {
  const [logoUrl, setLogoUrl] = React.useState(null);
  const { theme } = useTheme();

  // New format: /images/... — use directly
  const isNewFormat = teamLogo?.startsWith('/images/');
  // Legacy format: bare filename with extension (no /images/ prefix)
  const isLegacyFile = !isNewFormat && teamLogo && teamLogo.includes('.');

  React.useEffect(() => {
    if (isLegacyFile && teamId) {
      teamImageService.getTeamLogoUrl(teamId, teamLogo)
        .then(url => { if (url) setLogoUrl(url); })
        .catch(err => console.error('Failed to load team logo:', err));
    } else {
      setLogoUrl(null);
    }
  }, [teamId, teamLogo, isLegacyFile]);

  // New format: direct path
  if (isNewFormat) {
    return <img src={resolveImageUrl(teamLogo)} alt={teamName} className={`${size} ${rounded} object-cover`} />;
  }

  // Legacy format: presigned URL
  if (isLegacyFile && logoUrl) {
    return <img src={logoUrl} alt={teamName} className={`${size} ${rounded} object-cover`} />;
  }

  // Emoji or icon logo
  if (teamLogo && !isLegacyFile) {
    return (
      <div className={`${size} ${theme.backgroundSecondary} ${rounded} flex items-center justify-center`}>
        <span className="text-xl">{teamLogo}</span>
      </div>
    );
  }

  // Fallback to first letter of team name
  return (
    <div className={`${size} ${theme.gradient} ${rounded} flex items-center justify-center`}>
      <span className={`text-white font-bold ${textSize}`}>
        {(teamName || 'T').charAt(0).toUpperCase()}
      </span>
    </div>
  );
};

export default TeamLogo;
